'use client';

import { useState, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import DataTable from '../../../components/DataTable';

import { useFuelPriceHistoryList, deleteFuelPriceHistory, fetchAllFuelPriceHistory } from '../../../stores/fuelPriceHistoryStore';
import { useCompanyList } from '../../../stores/companyStore';
import ActionMenu from '../../../components/ActionMenu';
import { useNotifier } from '../../../components/Notifier';
import DeleteConfirmDialog from '../../../components/DeleteConfirmDialog';
import DeletedFuelHistoryConfirmedDialog from './DeletedFuelHistoryConfirmedDialog';

import FuelPriceHistoryAddEdit from './FuelPriceHistoryAddEdit';
import { useLanguage } from '../../../utils/i18n';

function FuelPriceHistoryPageContent() {
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const { t } = useLanguage();
  const [searchField, setSearchField] = useState('license_plate');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [strictConfirmOpen, setStrictConfirmOpen] = useState(false);
  const [confirmTargetId, setConfirmTargetId] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);
  const notify = useNotifier();

  // Search options follow the columns shown in the table (keys mapped in fuelPriceHistoryStore)
  const searchFieldOptions = useMemo(
    () => [
      { value: 'all', label: t('general.searchAll', 'All') },
      { value: 'company_name', label: t('general.company', 'Company') },
      { value: 'license_plate', label: 'License Plate' },
      { value: 'vehicle_name', label: 'Vehicle Name' },
      { value: 'fuel_type_name', label: 'Fuel Type' },
      { value: 'fuel_price_desc', label: t('general.description', 'Description') },
      { value: 'price', label: 'Price (IDR)' },
      { value: 'valid_at', label: 'Valid At' }
    ],
    [t]
  );

  const { companyOptions } = useCompanyList();

  const [selectedCompanies, setSelectedCompanies] = useState(() => {
    try {
      const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
      return stored ? [{ value: String(stored), label: String(stored) }] : [];
    } catch {
      return [];
    }
  });

  const companyOptionsNormalized = useMemo(
    () =>
      (companyOptions || []).map((c) => ({
        value: String(c.value ?? c.id ?? c),
        label: c.label ?? c.name ?? String(c.value ?? c.id ?? c)
      })),
    [companyOptions]
  );

  useEffect(() => {
    try {
      if (!companyOptionsNormalized || companyOptionsNormalized.length === 0) return;
      const raw = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
      if (!raw) return;
      let u = null;
      try {
        u = JSON.parse(raw);
      } catch {
        return;
      }
      const companyId = u?.company ?? u?.company_id ?? null;
      if (companyId == null) return;
      const companyIdStr = String(companyId);
      const match = companyOptionsNormalized.find((o) => String(o.value) === companyIdStr);
      const selected = match
        ? [{ value: String(match.value), label: match.label ?? String(match.value) }]
        : [{ value: companyIdStr, label: companyIdStr }];
      setSelectedCompanies((prev) => {
        const prevVal = prev && prev[0] && String(prev[0].value);
        if (prevVal === selected[0].value) return prev;
        return selected;
      });
    } catch (e) {
      console.error('auto select company from user failed', e);
    }
  }, [companyOptionsNormalized]);

  const filterCompanyIds = useMemo(() => {
    if (!selectedCompanies || selectedCompanies.length === 0) return '';
    const hasAll = selectedCompanies.some((s) => String(s.value) === 'all');
    if (hasAll) return '';
    return selectedCompanies.map((s) => String(s.value)).join(',');
  }, [selectedCompanies]);

  // --- 1. DEBOUNCE SEARCH INPUT ---
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = String(searchRaw ?? '').trim();
      setSearchValue((prev) => (prev === next ? prev : next));
      // keep same object when already on page 0 so it does not trigger an extra request
      setPaginationModel((p) => (p.page === 0 ? p : { ...p, page: 0 }));
    }, 700);

    return () => clearTimeout(timer);
  }, [searchRaw]);

  // --- 2. DATA FETCHING (single source for column search and 'all' search) ---
  const {
    data: rows,
    isLoading,
    totalRecord: totalRecords
  } = useFuelPriceHistoryList(paginationModel.page, paginationModel.pageSize, searchField, searchValue, filterCompanyIds);

  const handleFetchAllForExport = async () => {
    try {
      return await fetchAllFuelPriceHistory({ searchKey: searchField, searchValue, companyId: filterCompanyIds, total: totalRecords });
    } catch (err) {
      console.error('export all error', err);
      return [];
    }
  };

  const handlePageChange = (model) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) {
        return prev;
      }
      return model;
    });
  };

  const handleAddClick = () => {
    setEditingId(null);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const handleCompanyChange = (options = []) => {
    const sanitized = (options || []).map((o) => ({ value: String(o.value), label: o.label ?? String(o.value) }));
    setSelectedCompanies(sanitized);
    setPaginationModel((p) => (p.page === 0 ? p : { ...p, page: 0 }));
    try {
      if (typeof window !== 'undefined') {
        if (sanitized.length === 1) localStorage.setItem('companyId', String(sanitized[0].value));
        else localStorage.removeItem('companyId');
      }
    } catch {
      console.log('eror');
    }
  };

  const formatDeleteTargetText = (row) => {
    if (!row) return '';
    const plate = row.license_plate || row?.vehicle_join?.license_plate || row?.join?.license_plate || row?.vehicle_name || '-';
    const fuelName = row.fuel_type_name || row?.fuel_type_join?.name || row?.join?.fuel_type_name || '-';
    const validAt =
      row.valid_at && !String(row.valid_at).startsWith('0001-01-01') && !String(row.valid_at).startsWith('1900-01-01')
        ? dayjs(row.valid_at).format('DD MMM YYYY')
        : '-';
    const price = row.price != null && row.price !== '' ? `Rp. ${Number(row.price).toLocaleString('id-ID')}` : '-';

    return `${plate} - ${fuelName} - ${validAt} - ${price}`;
  };

  const handleDelete = (row) => {
    setConfirmTargetId(row.id);
    setConfirmName(formatDeleteTargetText(row));
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    if (confirmLoading) return;
    setConfirmOpen(false);
    setConfirmTargetId(null);
    setConfirmName('');
  };

  const handleConfirmDelete = () => {
    setConfirmOpen(false);
    setStrictConfirmOpen(true);
  };

  const handleFinalDelete = async () => {
    if (!confirmTargetId) return;
    setConfirmLoading(true);
    try {
      await deleteFuelPriceHistory(confirmTargetId);
      notify({ message: t('notify.deleted', 'Deleted successfully'), severity: 'success' });
      setPaginationModel((p) => ({ ...p }));
    } catch (err) {
      console.error(err);
      notify({ message: t('notify.deleteFailed', 'Failed to delete'), severity: 'error' });
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setStrictConfirmOpen(false);
      setConfirmTargetId(null);
      setConfirmName('');
    }
  };

  const handleDialogSuccess = () => {
    setPaginationModel((p) => ({ ...p }));
    handleDialogClose();
  };

  const handleSearch = ({ field, value }) => {
    // page reset + searchValue update are handled by the debounce effect
    if (field && field !== searchField) setSearchField(field);
    setSearchRaw(value ?? '');
  };

  const handleSearchFieldChange = (f) => {
    setSearchField(f || 'all');
    setSearchRaw('');
    setSearchValue('');
    setPaginationModel((p) => (p.page === 0 ? p : { ...p, page: 0 }));
  };

  const getCompanyLabel = (row) => {
    if (!row) return '-';
    if (row?.join?.company_name) return row.join.company_name;
    const companyId = row?.company ?? row?.company_id ?? null;
    if (companyId && companyOptionsNormalized) {
      const found = companyOptionsNormalized.find((c) => String(c.value) === String(companyId));
      if (found) return found.label;
    }
    return '-';
  };

  const dtColumns = [
    {
      field: 'company',
      headerName: t('general.company', 'Company'),
      flex: 1,
      minWidth: 150,
      renderCell: (params) => getCompanyLabel(params.row)
    },
    {
      field: 'license_plate',
      headerName: 'License Plate',
      flex: 1,
      minWidth: 130,
      valueGetter: (value, row) => row.license_plate || row?.vehicle_join?.license_plate || '-'
    },
    {
      field: 'vehicle_name',
      headerName: 'Vehicle Name',
      flex: 1,
      minWidth: 150,
      valueGetter: (value, row) => row.vehicle_name || row?.vehicle_join?.name || '-'
    },
    {
      field: 'fuel_type_name',
      headerName: 'Fuel Type',
      flex: 1,
      minWidth: 150,
      valueGetter: (value, row) => row.fuel_type_name || row?.fuel_type_join?.name || '-'
    },
    {
      field: 'description',
      headerName: t('general.description', 'Description'),
      flex: 1,
      minWidth: 180,
      valueGetter: (value, row) => row.description || row.fuel_price_desc || '-'
    },
    {
      field: 'price',
      headerName: 'Price (IDR)',
      flex: 1,
      minWidth: 130,
      valueGetter: (value, row) => {
        const pr = row.price;
        if (pr === undefined || pr === null) return '-';
        return 'Rp ' + Number(pr).toLocaleString('id-ID');
      }
    },
    {
      field: 'valid_at',
      headerName: 'Valid At',
      flex: 1,
      minWidth: 150,
      valueGetter: (value, row) => {
        const val = row.valid_at;
        if (!val || String(val).startsWith('0001-01-01') || String(val).startsWith('1900-01-01')) return '-';
        return dayjs(val).isValid() ? dayjs(val).format('DD MMM YYYY') : '-';
      }
    },
    {
      field: 'actions',
      headerName: t('general.actions', 'Actions'),
      sortable: false,
      width: 100,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => <ActionMenu mode="inline" row={params.row} onDelete={() => handleDelete(params.row)} />
    }
  ];

  return (
    <div>
      <DataTable
        title="Fuel Price History List"
        onFetchAllForExport={handleFetchAllForExport}
        rows={rows}
        columns={dtColumns}
        rowCount={totalRecords}
        loading={isLoading}
        pageSizeOptions={[10, 25, 50]}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        searchQuery={searchRaw}
        onSearch={({ field, value }) => handleSearch({ field, value })}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onSearchFieldChange={handleSearchFieldChange}
        onAddClick={handleAddClick}
        searchLabel="Search here"
        companyOptions={companyOptionsNormalized}
        selectedCompanies={selectedCompanies}
        onCompanyChange={handleCompanyChange}
      />

      <FuelPriceHistoryAddEdit open={openDialog} id={editingId} onClose={handleDialogClose} onSuccess={handleDialogSuccess} />

      <DeleteConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        onClose={handleConfirmClose}
        onConfirm={handleConfirmDelete}
        title={t('deleteConfirm.title', 'Confirm Delete')}
        message={t('deleteConfirm.message', 'Are you sure you want to delete this item?')}
        confirmLabel={t('deleteConfirm.label', 'Delete')}
        cancelLabel={t('deleteConfirm.cancel', 'Cancel')}
        itemName={confirmName}
      />

      <DeletedFuelHistoryConfirmedDialog
        open={strictConfirmOpen}
        loading={confirmLoading}
        onClose={() => {
          if (!confirmLoading) setStrictConfirmOpen(false);
        }}
        onConfirm={handleFinalDelete}
        targetText={confirmName}
      />
    </div>
  );
}

export default function FuelPriceHistoryPage() {
  return (
    <>
      <FuelPriceHistoryPageContent />
    </>
  );
}
